import { SearchOutlined } from '@ant-design/icons';
import { Button, Card, Col, Input, Row, Select, Space, Spin, Table, Typography, message } from 'antd';
import { MdOutlineKeyboardArrowLeft, MdRefresh } from "react-icons/md";
import { useNavigate } from 'react-router-dom';
import React, { useEffect, useState, useCallback } from 'react';
import { getAllApplicantAdmissionRequirementsStatusApiRequest, updateApplicantAdmissionRequirementsStatusApiRequest, updateStatusOfRequirmentsAdminApiRequest } from '../../../api/applicant';
import Header from '../../UserAdmin/NewHeader';
import SideNav from '../../UserAdmin/SideNav';

import ApplicantReviewModal from './ApplicantReviewModal';
import { columns } from './utils';

const { Title } = Typography;
const { Option } = Select;

const ManageReviewApplicants = () => {
    const [isSideNavOpen, setIsSideNavOpen] = useState(false);
    const [isSmallScreen, setIsSmallScreen] = useState(window.innerWidth < 768);
    const [loading, setLoading] = useState(false);
    const [applicants, setApplicants] = useState([]);
    const [allApplicants, setAllApplicants] = useState([]); // Store all unfiltered applicants
    const [pagination, setPagination] = useState({
        current: 1,
        pageSize: 10,
        total: 0
    });
    const [searchText, setSearchText] = useState('');
    const [academicLevelFilter, setAcademicLevelFilter] = useState(null);
    const [academicStrandFilter, setAcademicStrandFilter] = useState(null);
    const [modalVisible, setModalVisible] = useState(false);
    const [selectedApplicant, setSelectedApplicant] = useState(null);
    const [actionLoading, setActionLoading] = useState(false);
    const navigate = useNavigate();
    const handleBack = () => navigate('/admin/dashboard');

    const toggleSideNav = useCallback(() => setIsSideNavOpen(prev => !prev), []);

    useEffect(() => {
        const handleResize = () => setIsSmallScreen(window.innerWidth < 768);
        window.addEventListener('resize', handleResize);
        handleResize();
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const fetchApplicants = async (page = 1, limit = 10) => {
        setLoading(true);
        try {
            // Prepare to fetch applicants with filters

            const response = await getAllApplicantAdmissionRequirementsStatusApiRequest(page, limit, {
                searchText: searchText.trim(),
                academicLevel: academicLevelFilter,
                academicStrand: academicStrandFilter
            });
            console.log('ress22', response.data)
            if (response.success) {
                setAllApplicants(response.data.applicants);
                setApplicants(response.data.applicants);
                setPagination({
                    current: response.data.pagination.page,
                    pageSize: response.data.pagination.limit,
                    total: response.data.pagination.totalDocs
                });
            } else {
                message.error('Failed to fetch applicants');
            }
        } catch (error) {
            console.error('Error fetching applicants:', error);
            message.error('Failed to fetch applicants: ' + (error.message || 'Unknown error'));
        } finally {
            setLoading(false);
        }
    };

    // Function to apply filters to the applicants data
    const applyFilters = () => {
        setLoading(true);
        let filteredData = [...allApplicants];

        // Filter by search text (name, email, or ID)
        if (searchText.trim() !== '') {
            const searchLower = searchText.trim().toLowerCase();
            filteredData = filteredData.filter(applicant =>
                (applicant.firstName && applicant.firstName.toLowerCase().includes(searchLower)) ||
                (applicant.lastName && applicant.lastName.toLowerCase().includes(searchLower)) ||
                (applicant.email && applicant.email.toLowerCase().includes(searchLower)) ||
                (applicant.studentId && applicant.studentId.toString().toLowerCase().includes(searchLower))
            );
        }

        // Filter by academic level
        if (academicLevelFilter) {
            filteredData = filteredData.filter(applicant =>
                applicant.academicLevel === academicLevelFilter
            );
        }

        // Filter by academic strand
        if (academicStrandFilter) {
            filteredData = filteredData.filter(applicant =>
                applicant.academicStrand === academicStrandFilter
            );
        }

        setApplicants(filteredData);
        // Update pagination to reflect filtered results
        setPagination(prev => ({
            ...prev,
            total: filteredData.length
        }));
        setLoading(false);
    };

    useEffect(() => {
        fetchApplicants(pagination.current, pagination.pageSize);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Apply filters whenever filter criteria change
    useEffect(() => {
        if (allApplicants.length > 0) {
            applyFilters();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchText, academicLevelFilter, academicStrandFilter]);

    const handleTableChange = (paginationParams) => {
        // Update pagination and fetch data with new pagination parameters
        fetchApplicants(paginationParams.current, paginationParams.pageSize);
    };

    const handleSearch = () => {
        applyFilters();
    };

    const handleOpenModal = (applicant) => {
        setSelectedApplicant(applicant);
        setModalVisible(true);
    };

    const handleCloseModal = () => {
        setModalVisible(false);
        setSelectedApplicant(null);
    };

    const handleApproveRequirementsForEnrollmentAdmin = async (applicantId) => {
        setActionLoading(true);
        try {
            // Use the updateStatusOfAdmissionRequirementsApiRequest function instead
            const response = await updateStatusOfRequirmentsAdminApiRequest(applicantId, 'Complete');
            if (response.success) {
                message.success('Applicant requirements approved successfully');
                fetchApplicants(pagination.current, pagination.pageSize);
                return true;
            } else {
                message.error('Failed to approve requirements');
                return false;
            }
        } catch (error) {
            console.error('Error approving requirements:', error);
            message.error('Failed to approve requirements: ' + (error.message || 'Unknown error'));
            return false;
        } finally {
            setActionLoading(false);
        }
    };

    const handleRejectRequirementsForEnrollmentAdmin = async (applicantId, reason) => {
        setActionLoading(true);
        try {
            // Use the updateStatusOfAdmissionRequirementsApiRequest function instead
            const response = await updateStatusOfRequirmentsAdminApiRequest(applicantId, 'Rejected', reason);
            if (response.success) {
                message.success('Applicant requirements rejected successfully');
                fetchApplicants(pagination.current, pagination.pageSize);
                return true;
            } else {
                message.error('Failed to reject requirements');
                return false;
            }
        } catch (error) {
            console.error('Error rejecting requirements:', error);
            message.error('Failed to reject requirements: ' + (error.message || 'Unknown error'));
            return false;
        } finally {
            setActionLoading(false);
        }
    };


    const handleApproveRequirements = async (applicantId) => {
        setActionLoading(true);
        try {
            const response = await updateApplicantAdmissionRequirementsStatusApiRequest(applicantId, 'Complete');
            if (response.success) {
                message.success('Applicant requirements approved successfully');
                fetchApplicants(pagination.current, pagination.pageSize);
                return true;
            } else {
                message.error('Failed to approve requirements');
                return false;
            }
        } catch (error) {
            console.error('Error approving requirements:', error);
            message.error('Failed to approve requirements: ' + (error.message || 'Unknown error'));
            return false;
        } finally {
            setActionLoading(false);
        }
    };

    const handleRejectRequirements = async (applicantId, reason) => {
        setActionLoading(true);
        try {
            const response = await updateApplicantAdmissionRequirementsStatusApiRequest(applicantId, 'Rejected', reason);
            if (response.success) {
                message.success('Applicant requirements rejected successfully');
                fetchApplicants(pagination.current, pagination.pageSize);
                return true;
            } else {
                message.error('Failed to reject requirements');
                return false;
            }
        } catch (error) {
            console.error('Error rejecting requirements:', error);
            message.error('Failed to reject requirements: ' + (error.message || 'Unknown error'));
            return false;
        } finally {
            setActionLoading(false);
        }
    };


    // Update columns to include the onClick handler
    const tableColumns = columns.map(col => {
        if (col.key === 'actions') {
            return {
                ...col,
                render: (_, record) => (
                    <Button
                        type="primary"
                        icon={<SearchOutlined />}
                        onClick={() => handleOpenModal(record)}
                    >
                        Review Applicant
                    </Button>
                )
            };
        }
        return col;
    });

    return (
        <div className={`admin-page-container ${isSideNavOpen ? 'admin-sidenav-open' : ''}`}>
            <Header onMenuClick={toggleSideNav} isSideNavOpen={isSideNavOpen} />
            <div className="admin-content-wrapper">
                <div className="admin-sidenav-container">
                    <SideNav isOpen={isSideNavOpen} onClose={toggleSideNav} isSmallScreen={isSmallScreen} />
                </div>
                <main className="admin-main-content-area">
                    <Card className="mb-4">
                        <div className="page-title">
                            <p className="heading">Manage Document Requirements</p>
                        </div>
                        {/* <Title level={4}>Review Applicants with Incomplete Requirements</Title> */}

                        <Row gutter={[16, 16]} className="mb-4" style={{ marginTop: '10px' }}>
                            <Col xs={24} sm={12} md={8} lg={6}>
                                <Input
                                    placeholder="Search by name, ID, or email"
                                    value={searchText}
                                    onChange={(e) => setSearchText(e.target.value)}
                                    prefix={<SearchOutlined />}
                                    onPressEnter={handleSearch}
                                />
                            </Col>
                            <Col xs={24} sm={12} md={8} lg={6}>
                                <Select
                                    placeholder="Academic Level"
                                    style={{ width: '100%' }}
                                    allowClear
                                    onChange={(value) => setAcademicLevelFilter(value)}
                                >
                                    <Option value="Grade 11">Grade 11</Option>
                                    <Option value="Grade 12">Grade 12</Option>
                                </Select>
                            </Col>
                            <Col xs={24} sm={12} md={8} lg={6}>
                                <Select
                                    placeholder="Academic Strand"
                                    style={{ width: '100%' }}
                                    allowClear
                                    onChange={(value) => setAcademicStrandFilter(value)}
                                >
                                    <Option value="ABM">ABM</Option>
                                    <Option value="HUMSS">HUMSS</Option>
                                    <Option value="STEM">STEM</Option>
                                    <Option value="GAS">GAS</Option>
                                    <Option value="TVL">TVL</Option>
                                </Select>
                            </Col>
                            <Col xs={24} sm={12} md={8} lg={6}>
                                <Space>
                                    <Button type="primary" onClick={handleSearch}>
                                        Search
                                    </Button>
                                    <Button onClick={() => {
                                        setSearchText('');
                                        setAcademicLevelFilter(null);
                                        setAcademicStrandFilter(null);
                                        setPagination({
                                            current: 1,
                                            pageSize: 10,
                                            total: 0
                                        });
                                        fetchApplicants(1, 10);
                                    }}>
                                        Reset
                                    </Button>
                                </Space>
                            </Col>
                        </Row>

                        <Spin spinning={loading}>
                            <Table
                                style={{ marginTop: '10px' }}
                                columns={tableColumns}
                                dataSource={applicants}
                                rowKey="_id"

                                pagination={{
                                    ...pagination,
                                    showSizeChanger: true,
                                    showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`
                                }}
                                onChange={handleTableChange}
                            />
                        </Spin>
                    </Card>
                </main>
            </div>
            <ApplicantReviewModal
                visible={modalVisible}
                onClose={handleCloseModal}
                applicant={selectedApplicant}
                onApprove={handleApproveRequirementsForEnrollmentAdmin}
                onReject={handleRejectRequirementsForEnrollmentAdmin}
                onRejectRequirements={handleRejectRequirements}
                onApproveRequirements={handleApproveRequirements}
            />
        </div>
    );
};

export default ManageReviewApplicants;